import React, { useState, memo } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { FileUploadPreview } from '../../components/FileUploadPreview';
import { MissingOrInvalidFiles } from '../../components/MissingOrInvalidFiles';
import { Upload, Form, Input, Button, Typography, Card } from '../../../../../components';
import { validateZip, validateExcel } from '../../utils/validation';
import { getMaxCompressedFileSizeMB, getMaxUncompressedFileSizeMB } from '../../utils/uploadLimits';
import { getBytesfromMB } from '../../../../../lib/helpers';
import { REQUIRED_CSV_FILES, REQUIRED_EXCEL_SHEETS } from '../../../../../constants/discovery';
const { Paragraph, Title, Text } = Typography;
const { Dragger } = Upload;

export const SelectRVToolsFile = memo(({ resetForm, fileList, setFileList, handleDelete, completeStep, form }) => {
    const [validationError, setValidationError] = useState(null);
    const maxCompressedSize = getMaxCompressedFileSizeMB();
    const maxUncompressedSize = getMaxUncompressedFileSizeMB();

    const uploadProps = {
        name: 'rvtoolsCSVUpload',
        multiple: false,
        accept: '.zip, .xlsx',
        beforeUpload: async (file) => {
            const maxSizeInBytes = getBytesfromMB(maxCompressedSize);
            if (file.size > maxSizeInBytes) {
                setValidationError({
                    message: (
                        <>
                            <Title level={2}>File size error</Title>
                            <Paragraph>File size exceeds the maximum allowed size of {maxCompressedSize} MB.</Paragraph>
                        </>
                    ),
                });
                return Upload.LIST_IGNORE; // Prevents file from being used
            }
            const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
            const isValid = isExcel
                ? await validateExcel(file, REQUIRED_EXCEL_SHEETS, setValidationError)
                : await validateZip(file, maxUncompressedSize, REQUIRED_CSV_FILES, setValidationError);
            if (!isValid) {
                return Upload.LIST_IGNORE; // Prevents file from being used
            }
            return false; // Prevents automatic upload after successfully validating zip file
        },
        fileList: fileList,
        onChange(info) {
            setFileList(info.fileList);
        },
    };

    return (
        <>
            {validationError ? (
                <MissingOrInvalidFiles error={validationError} resetForm={resetForm} />
            ) : (
                <Form layout="vertical" form={form}>
                    <Form.Item
                        label={
                            <>
                                <Text>Import Name</Text>&nbsp;
                                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>(optional)</Text>
                            </>
                        }
                        name="importName"
                        extra="Automatically created if left blank"
                    >
                        <Input placeholder="Import Name" />
                    </Form.Item>
                    <Card className="upload-card">
                        <Form.Item
                            name="rvtoolsCSVUpload"
                            rules={[{ required: true, message: 'Please upload a zip file' }]}
                        >
                            {fileList.length === 0 ? (
                                <Dragger className="custom-upload" {...uploadProps}>
                                    <div className="ant-upload-drag-icon">
                                        <InboxOutlined className="upload-icon" />
                                    </div>
                                    <Text strong>Click or drag file to this area to upload</Text>
                                    <Paragraph className="text-light">
                                        Support for a single Zip or Excel file ({maxCompressedSize}MB max)
                                    </Paragraph>
                                </Dragger>
                            ) : (
                                <FileUploadPreview file={fileList[0]} handleDelete={handleDelete} />
                            )}
                        </Form.Item>
                    </Card>
                    <Form.Item className="button-group" style={{ marginBottom: 0 }}>
                        <div className="button-group">
                            <Button type="default" size="medium" disabled={fileList.length === 0} onClick={resetForm}>
                                Cancel
                            </Button>
                            <Button
                                onClick={completeStep}
                                type="primary"
                                htmlType="submit"
                                size="medium"
                                disabled={fileList.length === 0}
                            >
                                Continue
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            )}
        </>
    );
});
